import React, { useEffect, useState } from "react";
import { Instagram, Facebook } from "lucide-react";
import { api } from "@/lib/api";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V6.92a4.85 4.85 0 0 1-1.84-.23z" />
  </svg>
);

export default function Footer() {
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  return (
    <footer id="footer" className="bg-[var(--surface)] border-t border-[var(--border)] mt-10" data-testid="footer">
      <div className="container-nk py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-4">
          {/* Col 1 */}
          <div>
            <div className="mb-5">
              <img
                src="/naami-wordmark.png"
                alt="Naami Kitchens United"
                className="h-12 w-auto"
                style={{ filter: "invert(1) brightness(1.05)" }}
              />
            </div>
            <p className="text-[14px] text-muted leading-relaxed font-light max-w-[280px]">
              Scotland's largest purpose-built delivery kitchens. Twenty brands.
              One roof. Edinburgh.
            </p>
            <div className="flex gap-4 mt-6">
              <a href="#" aria-label="Instagram" className="text-muted hover:text-gold transition-colors" data-testid="footer-instagram">
                <Instagram size={18} />
              </a>
              <a href="#" aria-label="Facebook" className="text-muted hover:text-gold transition-colors" data-testid="footer-facebook">
                <Facebook size={18} />
              </a>
              <a href="#" aria-label="TikTok" className="text-muted hover:text-gold transition-colors" data-testid="footer-tiktok">
                <TikTokIcon size={17} />
              </a>
            </div>
          </div>

          {/* Col 2 */}
          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold mb-5">
              Our Brands
            </h4>
            <ul className="flex flex-col gap-2.5">
              {brands.map((b) => (
                <li key={b.id}>
                  <a
                    href="#brands"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById("brands")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-[13.5px] text-[var(--text)]/75 hover:text-gold transition-colors inline-flex items-center gap-2"
                    data-testid={`footer-brand-${b.name.replace(/\s+/g, "-")}`}
                  >
                    {b.name}
                    {b.is_coming_soon && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted">· soon</span>
                    )}
                    {b.is_opening_soon && !b.is_coming_soon && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-gold">· opening soon</span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 */}
          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold mb-5">
              Order Via
            </h4>
            <ul className="flex flex-col gap-2.5 text-[13.5px]">
              <li><a href="#" className="text-[var(--text)]/75 hover:text-gold transition-colors">Just Eat</a></li>
              <li><a href="#" className="text-[var(--text)]/75 hover:text-gold transition-colors">Deliveroo</a></li>
              <li><a href="#" className="text-[var(--text)]/75 hover:text-gold transition-colors">Uber Eats</a></li>
              <li><a href="#" className="text-[var(--text)]/75 hover:text-gold transition-colors">Panda</a></li>
              <li><a href="#" className="text-[var(--text)]/75 hover:text-gold transition-colors">Direct Brand Sites</a></li>
            </ul>
          </div>

          {/* Col 4 */}
          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold mb-5">
              Info
            </h4>
            <ul className="flex flex-col gap-2.5 text-[13.5px]">
              <li><a href="#about" className="text-[var(--text)]/75 hover:text-gold transition-colors">About Us</a></li>
              <li><a href="#partner" className="text-[var(--text)]/75 hover:text-gold transition-colors">Rent a Unit / Partner</a></li>
              <li><a href="#club" className="text-[var(--text)]/75 hover:text-gold transition-colors">Join the Club</a></li>
              <li><a href="/press" className="text-[var(--text)]/75 hover:text-gold transition-colors" data-testid="footer-press-link">Press &amp; Media Kit</a></li>
              <li><a href="#" className="text-[var(--text)]/75 hover:text-gold transition-colors">Allergen Info</a></li>
              <li><a href="mailto:hello@naamikitchens.com" className="text-[var(--text)]/75 hover:text-gold transition-colors" data-testid="footer-contact">Contact</a></li>
              <li><a href="/admin/login" className="text-muted hover:text-gold transition-colors text-[12px]" data-testid="footer-admin-link">Admin</a></li>
            </ul>
          </div>
        </div>

        <div className="divider mt-12 mb-6" />

        <div className="flex flex-col md:flex-row justify-between gap-4 text-[12px] text-muted font-mono uppercase tracking-[0.14em]">
          <p>© {new Date().getFullYear()} Naami Kitchens · Edinburgh</p>
          <p>Open 7 days · 11:00 – 23:00 · Edinburgh, Scotland</p>
        </div>
      </div>
    </footer>
  );
}
